import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import ExportButton from "../../components/ExportButton";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, X, RotateCcw, Trash2 } from "lucide-react";

const PROJECT_TYPES = ["Statutory Audit","Tax Audit","GST","Income Tax","Due Diligence","Internal Audit","Advisory","Management Support Services","ROC Compliance","Litigation","Other"];

const EMPTY = {
  project_name: "", client_id: "", project_type: "Statutory Audit",
  start_date: "", expected_end_date: "", budget_hours: 0, budget_amount: 0,
  billing_type: "Hourly", project_manager_id: "", team_member_ids: [],
  status: "Open", description: "",
};

export default function Projects() {
  const [projTab, setProjTab] = useState("active");
  const { user } = useAuth();
  const canManage = ["partner","admin","manager"].includes(user?.role);
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [groupByClient, setGroupByClient] = useState(false);
  const [filterClient, setFilterClient] = useState("");

  const load = () => Promise.all([
    api.get("/projects").then((r) => setList(r.data)),
    api.get("/clients").then((r) => setClients(r.data)),
    api.get("/users").then((r) => setUsers(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    // Default partner users into team
    const defaultPartners = users.filter((u) => u.role === "partner").map((u) => u.id);
    setForm({ ...EMPTY, team_member_ids: defaultPartners });
    setEditId(null); setOpen(true);
  };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p, team_member_ids: p.team_member_ids || [] }); setEditId(p.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, budget_hours: Number(form.budget_hours), budget_amount: Number(form.budget_amount) };
      if (editId) {
        await api.patch(`/projects/${editId}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const closeProject = async (id) => {
    if (!window.confirm("Close this project? Status will be set to Completed.")) return;
    try { await api.post(`/projects/${id}/close`); toast.success("Project closed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const reopenProject = async (id) => {
    if (!window.confirm("Re-open this completed project?")) return;
    try { await api.post(`/projects/${id}/reopen`); toast.success("Project reopened"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Permanently delete this project? This cannot be undone.")) return;
    try { await api.delete(`/projects/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleTeam = (uid) => setForm((s) => ({
    ...s,
    team_member_ids: s.team_member_ids.includes(uid) ? s.team_member_ids.filter((x) => x !== uid) : [...s.team_member_ids, uid],
  }));

  const statusColor = (s) => ({
    "Open": "border-blue-200 text-blue-700 bg-blue-50",
    "In Progress": "border-amber-200 text-amber-700 bg-amber-50",
    "Completed": "border-emerald-200 text-emerald-700 bg-emerald-50",
    "On Hold": "border-slate-200 text-slate-700 bg-slate-50",
  }[s] || "border-slate-200 text-slate-700 bg-slate-50");

  return (
    <div className="space-y-6 fade-in" data-testid="projects-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Projects</div>
          <h1 className="font-sans text-4xl mt-2">Engagement portfolio</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={filterClient || "_all"} onValueChange={(v)=>setFilterClient(v === "_all" ? "" : v)}>
            <SelectTrigger className="rounded-sm w-52" data-testid="project-filter-client"><SelectValue placeholder="All clients"/></SelectTrigger>
            <SelectContent><SelectItem value="_all">All clients</SelectItem>{clients.map((c)=><SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant={groupByClient ? "default" : "outline"} onClick={()=>setGroupByClient((g)=>!g)} className="rounded-sm" data-testid="project-group-toggle">{groupByClient ? "Flat view" : "Group by client"}</Button>
          {canManage && <ExportButton path="/exports/projects" filename="projects.csv" testId="projects-export"/>}
          {canManage && <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-project-btn"><Plus className="h-4 w-4 mr-2"/>New Project</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-sans text-2xl">{editId ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label className="eyebrow">Project Name</Label><Input required value={form.project_name} onChange={(e)=>setForm({...form, project_name: e.target.value})} className="mt-2 rounded-sm" data-testid="project-name-input"/></div>
                <div><Label className="eyebrow">Client</Label>
                  <Select value={form.client_id} onValueChange={(v)=>setForm({...form, client_id: v})}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="project-client-select"><SelectValue placeholder="Select client"/></SelectTrigger>
                    <SelectContent>{clients.map((c)=><SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Project Type</Label>
                  <Select value={form.project_type} onValueChange={(v)=>setForm({...form, project_type: v})}>
                    <SelectTrigger className="mt-2 rounded-sm"><SelectValue/></SelectTrigger>
                    <SelectContent>{PROJECT_TYPES.map((t)=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Status</Label>
                  <Select value={form.status} onValueChange={(v)=>setForm({...form, status: v})}>
                    <SelectTrigger className="mt-2 rounded-sm"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="Open">Open</SelectItem><SelectItem value="In Progress">In Progress</SelectItem><SelectItem value="On Hold">On Hold</SelectItem><SelectItem value="Completed">Completed</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Start Date</Label><Input type="date" value={form.start_date} onChange={(e)=>setForm({...form, start_date: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Expected End</Label><Input type="date" value={form.expected_end_date} onChange={(e)=>setForm({...form, expected_end_date: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Budget Hours</Label><Input type="number" value={form.budget_hours} onChange={(e)=>setForm({...form, budget_hours: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Budget Amount (₹)</Label><Input type="number" value={form.budget_amount} onChange={(e)=>setForm({...form, budget_amount: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Billing Type</Label>
                  <Select value={form.billing_type} onValueChange={(v)=>setForm({...form, billing_type: v})}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="project-billing-type"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="Hourly">Hourly</SelectItem><SelectItem value="Fixed Fee">Fixed Fee</SelectItem><SelectItem value="Retainership">Retainership</SelectItem><SelectItem value="Internal/Non-Chargeable">Internal / Non-Chargeable</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Project Manager</Label>
                  <Select value={form.project_manager_id} onValueChange={(v)=>setForm({...form, project_manager_id: v})}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="project-manager-select"><SelectValue placeholder="Select"/></SelectTrigger>
                    <SelectContent>{users.filter(u=>u.status !== "inactive").map((u)=><SelectItem key={u.id} value={u.id}>{u.name} <span className="text-xs text-slate-400">({u.role})</span></SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="eyebrow">Team Members</Label>
                  <div className="mt-2 border border-slate-200 rounded-sm p-3 max-h-40 overflow-y-auto grid grid-cols-2 gap-2">
                    {users.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded-sm">
                        <Checkbox checked={form.team_member_ids.includes(u.id)} onCheckedChange={()=>toggleTeam(u.id)}/>
                        <span>{u.name} <span className="text-xs text-slate-400">({u.role})</span></span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="col-span-2"><Label className="eyebrow">Description</Label><Textarea rows={3} value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="mt-2 rounded-sm"/></div>
                <DialogFooter className="col-span-2"><Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="project-save-btn">{busy ? "Saving..." : "Save"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>}
        </div>
      </div>

      <div data-testid="projects-list">
        {(() => {
          const done = (p) => ["Completed", "Closed"].includes(p.status);
          const byClient = filterClient ? list.filter((p) => p.client_id === filterClient) : list;
          const filteredList = byClient.filter((p) => (projTab === "completed" ? done(p) : !done(p)));
          const tabs = (
            <div className="flex gap-2 mb-4" data-testid="project-status-tabs">
              {[["active", "Active", byClient.filter((p) => !done(p)).length], ["completed", "Completed", byClient.filter(done).length]].map(([k, l, n]) => (
                <button key={k} onClick={() => setProjTab(k)} className={`px-4 h-9 text-sm border-b-2 ${projTab === k ? "border-[#946f30] font-semibold text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`} data-testid={`projects-tab-${k}`}>{l} ({n})</button>
              ))}
            </div>
          );
          if (filteredList.length === 0) {
            return <>{tabs}<div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-500">{projTab === "completed" ? "No completed projects." : "No active projects."}</div></>;
          }
          const renderCard = (p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-mono">{p.project_code}</div>
                  <Link to={`/portal/projects/${p.id}`} className="block">
                    <h3 className="font-sans text-2xl mt-1 truncate hover:text-[#946f30] transition-colors">{p.project_name}</h3>
                  </Link>
                  <div className="text-sm text-slate-600 mt-1">{p.client_name || "—"} · {p.project_type}</div>
                </div>
                <span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border rounded-full whitespace-nowrap ${statusColor(p.status)}`}>{p.status}</span>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-4 text-xs">
                <div><div className="eyebrow">Budget Hrs</div><div className="serif-num text-xl mt-1">{p.budget_hours || 0}</div></div>
                <div><div className="eyebrow">Budget ₹</div><div className="serif-num text-xl mt-1">{(p.budget_amount || 0).toLocaleString("en-IN")}</div></div>
                <div><div className="eyebrow">Billing</div><div className="text-sm mt-2">{p.billing_type}</div></div>
              </div>
              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between gap-2 text-xs">
                <div className="text-slate-500">Manager: <span className="text-slate-900">{p.project_manager_name || "—"}</span> · Team: {p.team_member_ids?.length || 0}</div>
                {canManage && <div className="flex items-center gap-2">
                  <button onClick={()=>openEdit(p)} className="text-slate-400 hover:text-slate-900" title="Edit" data-testid={`project-edit-${p.id}`}><Pencil className="h-4 w-4"/></button>
                  {p.status !== "Completed" && <button onClick={()=>closeProject(p.id)} className="text-slate-400 hover:text-emerald-700 inline-flex items-center gap-1" title="Close" data-testid={`project-close-${p.id}`}><X className="h-4 w-4"/>Close</button>}
                  {p.status === "Completed" && user?.role === "partner" && <button onClick={()=>reopenProject(p.id)} className="text-slate-400 hover:text-blue-700 inline-flex items-center gap-1" title="Reopen" data-testid={`project-reopen-${p.id}`}><RotateCcw className="h-4 w-4"/>Reopen</button>}
                  {user?.role === "partner" && <button onClick={()=>deleteProject(p.id)} className="text-slate-400 hover:text-red-600" title="Delete" data-testid={`project-delete-${p.id}`}><Trash2 className="h-4 w-4"/></button>}
                </div>}
              </div>
            </div>
          );
          if (!groupByClient) {
            return <>{tabs}<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 stagger" data-testid="projects-grid">{filteredList.map(renderCard)}</div></>;
          }
          // Group by client_id
          const groups = {};
          filteredList.forEach((p) => {
            const key = p.client_id || "_none";
            groups[key] = groups[key] || { name: p.client_name || "Unassigned", list: [] };
            groups[key].list.push(p);
          });
          return (
            <div className="space-y-6" data-testid="projects-grouped">{tabs}
              {Object.entries(groups).map(([cid, g]) => (
                <div key={cid} className="bg-white border border-slate-200 rounded-sm">
                  <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                    <Link to={cid !== "_none" ? `/portal/clients/${cid}` : "#"} className="font-sans text-lg hover:text-[#946f30]" data-testid={`group-client-${cid}`}>{g.name}</Link>
                    <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{g.list.length} project{g.list.length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">{g.list.map(renderCard)}</div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
